import React, { useState, useEffect } from 'react';
import { rankingsApi } from '../services/api';
import { RankingListResponse, StudyRanking } from '../types';

interface StudyRankingsProps {
  orgId?: number;
  currentUserId?: string;
}

const StudyRankings: React.FC<StudyRankingsProps> = ({
  orgId = 1,
  currentUserId,
}) => {
  const [rankings, setRankings] = useState<RankingListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [period, setPeriod] = useState('daily');
  const [group, setGroup] = useState('overall');
  const [groupValue, setGroupValue] = useState<string>('');

  // 순위 데이터 로드
  const loadRankings = async () => {
    try {
      setLoading(true);
      const response = await rankingsApi.getStudyRankings({
        orgId,
        period,
        group,
        groupValue: groupValue || undefined,
        limit: 10,
        offset: 0,
        currentUserId: currentUserId || undefined,
      });
      setRankings(response);
      setError(null);
    } catch (err) {
      console.error('순위 로드 실패:', err);
      setError('순위 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRankings();
  }, [period, group, groupValue, orgId, currentUserId]);

  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return rank;
    }
  };

  const getRankChangeIcon = (change: number) => {
    if (change > 0) return '↗️';
    if (change < 0) return '↘️';
    return '=';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                <div className="flex-1 h-4 bg-gray-300 rounded"></div>
                <div className="w-16 h-4 bg-gray-300 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-red-600 text-center">
          <p>{error}</p>
          <button
            onClick={loadRankings}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* 헤더 */}
      <div className="border-b border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">순공부 순위</h2>

        {/* 필터 */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* 기간 필터 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              기간
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="daily">일일</option>
              <option value="weekly">주간</option>
              <option value="monthly">월간</option>
              <option value="total">전체</option>
            </select>
          </div>

          {/* 그룹 필터 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              그룹
            </label>
            <select
              value={group}
              onChange={(e) => {
                setGroup(e.target.value);
                setGroupValue('');
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="overall">전체</option>
              <option value="grade">학년별</option>
              <option value="class">반별</option>
            </select>
          </div>

          {/* 그룹 값 필터 */}
          {(group === 'grade' || group === 'class') && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {group === 'grade' ? '학년' : '반'}
              </label>
              <input
                type="text"
                value={groupValue}
                onChange={(e) => setGroupValue(e.target.value)}
                placeholder={group === 'grade' ? '예: 고1' : '예: 1반'}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}
        </div>

        {/* 나의 현재 순위 */}
        {rankings?.current_user_rank && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium">
              나의 현재 순위: {rankings.current_user_rank}위
            </p>
          </div>
        )}
      </div>

      {/* 순위 목록 */}
      <div className="p-6">
        {rankings?.rankings && rankings.rankings.length > 0 ? (
          <div className="space-y-3">
            {rankings.rankings.map((ranking: StudyRanking, index: number) => (
              <div
                key={ranking.user_id}
                className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                  ranking.is_current_user
                    ? 'bg-blue-100 border-2 border-blue-300'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                {/* 순위 및 사용자 정보 */}
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <span className="text-lg font-bold">
                      {getRankBadge(ranking.rank)}
                    </span>
                  </div>

                  <div>
                    <p className="font-medium text-gray-900">
                      {ranking.name}
                      {ranking.is_current_user && (
                        <span className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                          나
                        </span>
                      )}
                    </p>
                    {(ranking.grade || ranking.class_name) && (
                      <p className="text-sm text-gray-500">
                        {ranking.grade} {ranking.class_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* 공부 시간 및 통계 */}
                <div className="text-right">
                  <p className="font-bold text-lg text-gray-900">
                    {ranking.study_hours}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    {ranking.session_count && (
                      <span>{ranking.session_count}회</span>
                    )}
                    {ranking.efficiency && (
                      <span>{ranking.efficiency.toFixed(1)}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">순위 데이터가 없습니다.</p>
          </div>
        )}

        {/* 페이지네이션 정보 */}
        {rankings && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              총 {rankings.total_users}명 중 {rankings.rankings.length}명 표시
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              최종 업데이트:{' '}
              {new Date(rankings.last_updated).toLocaleString('ko-KR')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyRankings;
